import {
  AfterViewInit,
  Component,
  Inject,
  NgZone,
  OnInit,
  ViewChild,
  ViewEncapsulation,
  afterNextRender,
  computed,
  inject,
  model,
  signal,
} from '@angular/core';
import { FormGroup } from '@angular/forms';
import { DialogRef } from '@progress/kendo-angular-dialog';
import { TabStripComponent } from '@progress/kendo-angular-layout';
import { take } from 'rxjs/operators';
import { TabStripDialogContentCSSSelector } from '../../../../core/constants-repository/app-style.constants';
import { UserOperation } from '../../../../core/enums/user-operation';
import { TimeBlockProjectModel } from '../../../../core/models/timeblock/time-block.model';
import { ShiveDialogService } from '../../../../core/services/controls/shive-dialog.service';
import { ShiveRenderService } from '../../../../core/services/rendering/shive-render.service';
import { TimeBlockHighlightingService } from '../../components/time-block/highlighted/time-block-highlighting.service';
import { DialogHttpRequestHandlerService } from '../../components/time-block/http/dialog-http-request-handler.service';
import { TimeBlockHttpCallbackService } from '../../components/time-block/http/time-block-http-callback.service';
import { CardinalPoints } from '../../components/time-block/interaction/movement/drag-and-resize/drag-part';
import { ITimeBlockComponentItem } from '../../components/time-block/types/time-block-component-item-types';
import {
  TimeBlockContentType,
  TimeBlockDialogItem,
  TimeBlockType,
} from '../../components/time-block/types/time-block-types';
import { CalendarService } from '../../services/calendar.service';
import { WINDOW } from '../../util/browser-window-ref';
import { TimeBlockDialogCSSSelector } from '../../util/calendar-style.constants';

@Component({
  selector: 'app-time-entry-dialog',
  templateUrl: './time-entry-dialog.component.html',
  styleUrls: ['./time-entry-dialog.component.scss'],
  encapsulation: ViewEncapsulation.None,
  standalone: false,
})
export class TimeEntryDialogComponent implements OnInit, AfterViewInit {
  @ViewChild('tabstrip') public tabstrip: TabStripComponent;
  // Inputs
  selectedHTMLElementBlock = model<HTMLElement>();
  selectedTimeBlockComponent = model<ITimeBlockComponentItem>();
  dialogRef = model<DialogRef>();
  dialogForm = model<FormGroup>();

  // Signals
  public workTimeStart = computed(
    () => this.calendarService.model.calendarProperties.workTimeStart,
  );
  public workTimeEnd = computed(() => this.calendarService.model.calendarProperties.workTimeEnd);
  public fixIntervalDropdownVisible = signal(false);
  public selectedContentType = signal<TimeBlockContentType>(TimeBlockContentType.Project);
  public noProjects = signal(false);
  public isInvoiced = computed(() => {
    const invoiceId = (this.selectedTimeBlockComponent().timeBlockModel as TimeBlockProjectModel)
      .invoiceId;
    return !!invoiceId;
  });
  public loading = signal(false);
  public updateButtonEnabled = computed(() => {
    // Disable save / update button when loading project tasks
    if (this.loading()) {
      return false;
    }

    // Disable save / update button if time block was already invoiced and at least one project exists for the project
    // view.
    const tab = this.selectedContentType();
    const invoiced = this.isInvoiced();
    const noProjects = this.noProjects();
    return tab !== TimeBlockContentType.Project || (!noProjects && !invoiced);
  });
  public deleteButtonEnabled = computed(() => {
    const nonActiveTimeBlock =
      this.selectedTimeBlockComponent().id >= 0 &&
      !this.selectedTimeBlockComponent().timeBlockModel.isActive;

    return nonActiveTimeBlock && !this.isInvoiced();
  });

  // Properties
  public readonly UserOperation = UserOperation;
  public readonly TimeBlockType = TimeBlockType;
  public readonly TimeBlockContentType = TimeBlockContentType;
  public readonly timeBlockComponentItem: ITimeBlockComponentItem;

  private readonly gap = 27; // Todo: Outsource
  private readonly dialogHttpRequestHandlerService = inject(DialogHttpRequestHandlerService);
  private readonly calendarService = inject(CalendarService);

  // DI
  private readonly ngZone = inject(NgZone);

  constructor(
    private readonly timeBlockHighlightingService: TimeBlockHighlightingService,
    @Inject(WINDOW) private readonly window: Window,
    private readonly shiveDialogService: ShiveDialogService,
    private readonly renderer: ShiveRenderService,
    private readonly timeBlockHttpCallbackService: TimeBlockHttpCallbackService,
  ) {
    afterNextRender(() => {
      if (!this.selectedHTMLElementBlock()) {
        return;
      }

      this.ngZone.onStable.pipe(take(1)).subscribe(() => {
        this.timeBlockHighlightingService.createHighlightedTimeBlockClone(
          this.selectedTimeBlockComponent(),
        );

        // DOM exists, styles applied, layout stable
        this.calcOffsetAndArrowPos(this.selectedHTMLElementBlock());
        this.renderer.removeClass(
          this.dialogRef().dialog.instance.dialog.nativeElement.parentElement,
          'opacity-0',
        );
      });
    });
  }

  ngOnInit(): void {
    this.selectedContentType.set(this.selectedTimeBlockComponent().timeBlockContentType);
  }

  ngAfterViewInit(): void {
    if (!this.selectedHTMLElementBlock()) {
      return;
    }

    this.tabstrip.selectTab(this.selectedContentType());
  }

  public sendAction(operation: UserOperation): void {
    const timeBlockComponent = this.selectedTimeBlockComponent();
    timeBlockComponent.timeBlockModel.fixInterval = null;

    const timeBlockDialogItem: TimeBlockDialogItem = {
      operation,
      timeBlockItem: this.selectedTimeBlockComponent(),
    };

    // Set the original content type here since we need to check it after updating the time block.
    this.timeBlockHttpCallbackService.selectedContentType =
      this.selectedTimeBlockComponent().timeBlockContentType;

    this.dialogHttpRequestHandlerService.init(
      this.dialogForm(),
      this.selectedContentType(),
      timeBlockDialogItem,
      this.fixIntervalDropdownVisible(),
    );
  }

  public cancel(): void {
    this.shiveDialogService.close(UserOperation.Cancel);
  }

  // Todo: Outsource
  private calcOffsetAndArrowPos(highlightedClone: HTMLElement): void {
    const dialog = document.querySelector(TimeBlockDialogCSSSelector);
    const dialogContent = document.querySelector(TabStripDialogContentCSSSelector);

    // Calculate horizontal offset
    const dialogHorizontalAlignment = this.calcHorizontalOffset(
      highlightedClone,
      dialog as HTMLElement,
    );

    if (dialogHorizontalAlignment === CardinalPoints.None) {
      return;
    }

    // Create triangle
    const triangle = this.renderer.createElement('div') as HTMLElement;
    this.renderer.addClass(triangle, 'triangle');
    this.renderer.addClass(triangle, 'large');

    // Append triangle and set horizontal position
    this.appendTriangle(triangle, dialog as HTMLElement, dialogHorizontalAlignment);
    // Calculate arrow position
    this.setVerticalArrowPos(
      dialogHorizontalAlignment,
      dialogContent as HTMLElement,
      highlightedClone,
      triangle,
    );
  }

  private calcHorizontalOffset(highlightedClone: HTMLElement, dialog: HTMLElement): CardinalPoints {
    const dialogWidth = dialog.clientWidth;

    const tbLeftToLeft = highlightedClone.getBoundingClientRect().left;
    const tbRightToRight = this.window.innerWidth - highlightedClone.getBoundingClientRect().right;

    if (tbLeftToLeft > dialogWidth + this.gap) {
      const offsetX = tbLeftToLeft - dialogWidth - this.gap;
      this.renderer.setStyle(dialog, 'left', `${offsetX}px`);
      return CardinalPoints.West;
    } else if (tbRightToRight > dialogWidth + this.gap) {
      const offsetX = tbRightToRight - dialogWidth - this.gap;
      this.renderer.setStyle(dialog, 'right', `${offsetX}px`);
      return CardinalPoints.East;
    }
    return CardinalPoints.None;
  }

  private setVerticalArrowPos(
    dialogHorizontalAlignment: CardinalPoints,
    dialogContent: HTMLElement,
    highlightedClone: HTMLElement,
    triangle: HTMLElement,
  ): void {
    const triangleHead =
      highlightedClone.getBoundingClientRect().bottom - highlightedClone.clientHeight / 2;

    const triangleHeight = triangle.clientHeight;
    const triangleHalfHeight = triangleHeight / 2;

    const dialogTop = dialogContent.getBoundingClientRect().top;
    const dialogBottom = dialogContent.getBoundingClientRect().bottom;
    if (
      triangleHead - triangleHalfHeight > dialogTop &&
      triangleHead + triangleHalfHeight < dialogBottom
    ) {
      if (dialogHorizontalAlignment === CardinalPoints.West) {
        this.renderer.removeClass(triangle, 'flipped');
      } else {
        this.renderer.addClass(triangle, 'flipped');
      }

      const triangleTop = triangleHead - 27;
      this.renderer.setStyle(triangle, 'top', `${triangleTop}px`);
      this.renderer.removeClass(triangle, '!k-display-none');
    } else {
      this.renderer.addClass(triangle, '!k-display-none');
    }
  }

  private appendTriangle(
    triangle: unknown,
    dialog: HTMLElement,
    dialogHorizontalAlignment: CardinalPoints,
  ): void {
    this.renderer.appendChild(dialog.parentElement, triangle);
    const offsetX =
      dialogHorizontalAlignment === CardinalPoints.West
        ? dialog.getBoundingClientRect().right - 37
        : dialog.getBoundingClientRect().left + 18;
    this.renderer.setStyle(triangle, 'left', `${offsetX}px`);
  }
}
